/*
  Why the website felt slow next to the app, and what this does about it.

  Measured against the live shop and the live backend on 14 October 2026:

    backend, first call after a quiet spell   10,522 ms
    backend, called again                        702 ms
    backend, called again                        682 ms
    /cart.js                                     223 ms
    checkout page HTML                         1,090 ms

  Ten and a half seconds. That is not our code being slow — it is Vercel
  starting the function from cold, which it does whenever nobody has called
  it for a few minutes. The app never meets it because the app talks to the
  backend from its very first screen; the website only talks to it at the
  cart, so a customer who presses BUY NOW on a product page is the one who
  pays for the cold start, standing in front of "Please wait…".

  Two things here, and neither touches what the shop sells:

  1. WARM. A one-line request to the backend the moment someone shows they
     are heading for the checkout — touching the buy button, the cart icon,
     the quantity box. It does nothing and returns nothing; its whole job is
     to be the call that waits ten seconds, so the real one does not.

  2. PREFETCH, on a mouse only. A desktop browser can fetch the next page
     while the pointer is still on its way to the button. Deliberately NOT
     done on a touch screen: a finger landing on a button is a tenth of a
     second from the tap anyway, so it would buy nothing and spend somebody's
     mobile data to do it.

  And a thin gold line across the top as soon as a tap commits to a new
  page, because a second of nothing at all is what "slow" actually feels
  like.
*/
(function () {
  'use strict';

  var BACKEND = 'https://admin.shriposhak.com';

  // Everything one click away from needing the backend.
  var ON_THE_WAY_TO_PAYING = [
    '[data-sp-buy-now]',
    '[name="add"]',
    '.sp-buy',
    '.spk-order',
    '.spk-pick__add',
    '[href*="your-cart"]',
    '[href*="sp-cart"]',
    '[href*="sp-checkout"]',
    '[href="/cart"]',
    '[href^="/cart?"]',
    '.quantity__input',
    '#cart-icon-bubble'
  ].join(',');

  /* ---------- 1. warm ---------- */

  // Vercel keeps a function warm for a few minutes after a call, so once a
  // minute is plenty. Cheap either way: the backend answers this before it
  // reads anything, talks to Shopify or opens Redis.
  var warmedAt = 0;

  function warm() {
    var now = Date.now();
    if (now - warmedAt < 60000) return;
    warmedAt = now;
    fetch(BACKEND + '/api/cart/sync-activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'warm' }),
      // Survives the page being left, which is the usual case here — the
      // customer taps Buy Now and the page starts unloading at once.
      keepalive: true
    }).catch(function () {
      // It is a warm-up. There is nothing to report and nothing to retry.
    });
  }

  /* ---------- 2. prefetch, mouse only ---------- */

  var asked = {};
  var askedCount = 0;

  // A mouse sweeping across a grid of poshak passes over a dozen of them.
  // Eight is enough to cover where somebody is actually going without
  // quietly pulling several megabytes they never look at.
  var PREFETCH_LIMIT = 8;

  // Links that DO something when they are opened. A prefetch is a real
  // request, and Shopify has a few of these.
  //
  // Matched against the PATH alone, and anchored. Written loosely as
  // `/checkouts?\b` it also matched our own /pages/checkout — which is the
  // one page the whole warm-up exists to make quick. A test caught it.
  var NEVER_PREFETCH = /^\/cart\/(change|clear|add|update)\b|^\/account\/logout\b|^\/checkouts?(\/|$)|\.(pdf|zip|jpg|jpeg|png|webp)$/i;

  function pathOf(url) {
    try {
      return new URL(url, window.location.href).pathname;
    } catch (e) {
      return url;
    }
  }

  // Somebody on a slow connection, or who has asked their phone to use less
  // data, is the last person to spend it on a page they have not opened.
  function worthPrefetching() {
    var link = navigator.connection;
    if (!link) return true;
    if (link.saveData) return false;
    return !/2g/.test(link.effectiveType || '');
  }

  function prefetch(url) {
    if (!url || asked[url] || askedCount >= PREFETCH_LIMIT) return;
    if (NEVER_PREFETCH.test(pathOf(url)) || !worthPrefetching()) return;
    asked[url] = true;
    askedCount += 1;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  function hrefOf(element) {
    if (!element.getAttribute) return null;
    // A link says where it goes in its href. A button that navigates by
    // itself — Buy Now — says so in data-sp-next, so the page it is about
    // to open can be fetched while the pointer is still on its way.
    var href = element.getAttribute('href') || element.getAttribute('data-sp-next');
    if (!href || href.charAt(0) === '#') return null;
    // Our own pages only. Prefetching somebody else's is not ours to do.
    if (/^https?:/i.test(href) && href.indexOf(window.location.origin) !== 0) return null;
    return href;
  }

  /* ---------- 3. the line across the top ---------- */

  var bar = null;
  var barTimer = 0;

  function startBar() {
    if (bar) return;
    bar = document.createElement('div');
    bar.className = 'sp-fastbar';
    document.body.appendChild(bar);
    // If we are somehow still on this page a few seconds later — a link
    // that opened a drawer instead of a page, a click something else
    // cancelled — the line tidies itself away rather than sitting there
    // forever, which would look like the shop had hung.
    clearTimeout(barTimer);
    barTimer = setTimeout(stopBar, 8000);
    // Reading a layout property lays it out at 0 right now, so what follows
    // is a change the browser can animate rather than where it began.
    // Not two animation frames: those never fire in a tab the browser is
    // not painting, and the line would simply never appear.
    void bar.offsetWidth;
    bar.style.transform = 'scaleX(0.75)';
  }

  function stopBar() {
    clearTimeout(barTimer);
    if (!bar) return;
    var going = bar;
    bar = null;
    going.style.transform = 'scaleX(1)';
    going.style.opacity = '0';
    setTimeout(function () {
      if (going.parentNode) going.parentNode.removeChild(going);
    }, 320);
  }

  function addStyles() {
    var style = document.createElement('style');
    style.textContent = [
      '.sp-fastbar{position:fixed;top:0;left:0;right:0;height:3px;z-index:10000;',
      'background:linear-gradient(90deg,#B8860B,#E8B84B);transform:scaleX(0);',
      'transform-origin:left;transition:transform 1.4s cubic-bezier(.1,.7,.3,1),opacity .3s ease;',
      'pointer-events:none}',
      '@media (prefers-reduced-motion: reduce){.sp-fastbar{transition:opacity .3s ease}}'
    ].join('');
    document.head.appendChild(style);
  }

  /* ---------- wiring ---------- */

  function wantsThisPage(event, link) {
    // A middle click, or ctrl/cmd-click, opens a new tab and leaves this
    // page exactly where it is — no bar, nothing is going anywhere.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    var href = hrefOf(link);
    if (!href) return false;
    // A link to a different spot on this same page is not a new page.
    return href.split('#')[0] !== window.location.pathname + window.location.search;
  }

  function hint(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var element = target.closest(ON_THE_WAY_TO_PAYING);
    if (!element) return;
    warm();
    if (event.type === 'mouseover') prefetch(hrefOf(element));
  }

  document.addEventListener('mouseover', hint, { passive: true });
  document.addEventListener('touchstart', hint, { passive: true });
  document.addEventListener('focusin', hint, { passive: true });

  // And every other link on the shop, on a mouse, after the pointer has
  // actually settled on it. A mouse takes a tenth of a second or so to
  // finish its journey and another to be clicked — long enough to have the
  // next page already in hand. Brushing past does not count: the wait below
  // is what tells "going there" apart from "going past".
  var dwell = 0;

  document.addEventListener(
    'mouseover',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var link = event.target.closest('a[href]');
      if (!link) return;
      var href = hrefOf(link);
      if (!href) return;
      clearTimeout(dwell);
      dwell = setTimeout(function () {
        prefetch(href);
      }, 65);
    },
    { passive: true }
  );

  document.addEventListener(
    'mouseout',
    function () {
      clearTimeout(dwell);
    },
    { passive: true }
  );

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var link = event.target.closest('a[href]');
    if (link && wantsThisPage(event, link)) {
      startBar();
      return;
    }

    // Our own buttons that go somewhere of their own accord.
    if (event.target.closest('[data-sp-buy-now]')) startBar();
  });

  // Coming BACK to this page from the browser's cache: the bar from the
  // click that left is still sitting there, finished, across the top.
  window.addEventListener('pageshow', stopBar);
  window.addEventListener('pagehide', stopBar);

  addStyles();
})();
